// Merge policy for a field-bearing legacy context (Story/Show/Season/…).
//
// Two element families describe the same entity. The bespoke Legacy* class
// ships hand-written card elements (StoryTitle, StoryContent, AuthorAvatar, …);
// the generic InspireEntity mints one DynamicApp*Element per field of the
// server-delivered def (Title, Body, Author Avatar, …). Both must stay
// RESOLVABLE — `availableElementVueComponents` is the single resolver the
// standalone published render uses, and a card built from either family has to
// find its component there or it renders "Invalid Element".
//
// Only the bespoke family belongs in the Elements PALETTE. The generic twins
// describe the same underlying fields under field-derived labels, so surfacing
// both listed every legacy field twice (Story Title AND Title, Story Content
// AND Body) and let an author drag a raw generic text element where the
// purpose-built one was intended. `hidden` is honoured exclusively by
// DynamicComponentEditorElementsGroup's palette filter and ignored by the
// resolver, so hiding — rather than dropping — satisfies both consumers.
// NOTE on the name filter below: it is NOT what prevents the duplicate labels.
// A generic element's `name` is its shared component name (every text field is
// "DynamicAppTextElement"), so it practically never equals a bespoke name —
// the duplicates were label collisions ("Author Name" twice) across different
// names, which only the hide step resolves. The filter stays as a cheap guard
// for the case where a bespoke element and a generic one genuinely share a
// name, where two entries for one type would otherwise reach the registry.
const mergeLegacyEntityElements = (bespokeElements, genericElements) => {
  const bespoke = (bespokeElements ?? []).filter(element => element?.name);
  const generic = (genericElements ?? []).filter(element => element?.name);

  // No bespoke class contributed anything — this is an ordinary generic entity
  // and its field elements ARE the palette. Hiding them would empty the group.
  if (bespoke.length === 0) {
    return generic;
  }

  const bespokeNames = new Set(bespoke.map(element => element.name));
  const hiddenGeneric = generic
      .filter(element => !bespokeNames.has(element.name))
      .map(element => ({ ...element, hidden: true }));

  return [...bespoke, ...hiddenGeneric];
};

module.exports = { mergeLegacyEntityElements };
